// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Star, MessageSquare, Calendar } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface Review {
  id: string;
  rating: number;
  review_text: string | null;
  reviewer_name: string;
  vendor_reply: string | null;
  replied_at: string | null;
  created_at: string;
}

interface VendorReviewManagementProps {
  vendorId: string;
}

export const VendorReviewManagement: React.FC<VendorReviewManagementProps> = ({
  vendorId
}) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, [vendorId]);

  const fetchReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('video_reviews')
        .select('*')
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReviews(data || []);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleReplySubmit = async (reviewId: string) => {
    if (!replyText.trim()) {
      toast.error('Please enter a reply');
      return;
    }

    setSubmittingReply(true);
    
    try {
      const { error } = await supabase
        .from('video_reviews')
        .update({
          vendor_reply: replyText.trim(),
          replied_at: new Date().toISOString()
        })
        .eq('id', reviewId);

      if (error) throw error;

      // Send notification email to customer
      try {
        const review = reviews.find(r => r.id === reviewId);
        if (review) {
          await invokeEdgeFunction('send-email', {
            body: {
              to: 'customer@email.com', // You'll need to get customer email from order
              subject: 'Vendor Reply to Your Review',
              html: `
                <h2>The vendor has replied to your review!</h2>
                <p><strong>Your Review:</strong> ${review.rating} stars</p>
                ${review.review_text ? `<p>"${review.review_text}"</p>` : ''}
                <p><strong>Vendor Reply:</strong> "${replyText.trim()}"</p>
                <p>Thank you for your feedback!</p>
              `
            }
          });
        }
      } catch (emailError) {
        console.error('Failed to send notification email:', emailError);
      }

      toast.success('Reply submitted successfully!');
      setReplyingTo(null);
      setReplyText('');
      fetchReviews();
    } catch (error) {
      console.error('Failed to submit reply:', error);
      toast.error('Failed to submit reply. Please try again.');
    } finally {
      setSubmittingReply(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
        }`}
      />
    ));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center">Loading reviews...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Customer Reviews ({reviews.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {reviews.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No reviews yet. Great reviews will help attract more customers!
          </div>
        ) : (
          reviews.map((review) => (
            <div key={review.id} className="border rounded-lg p-4 space-y-3">
              {/* Review Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    {renderStars(review.rating)}
                  </div>
                  <Badge variant="outline">
                    {review.rating} star{review.rating !== 1 ? 's' : ''}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  {formatDate(review.created_at)}
                </div>
              </div>

              {/* Review Content */}
              <div>
                <p className="font-medium">{review.reviewer_name}</p>
                {review.review_text && (
                  <p className="text-muted-foreground mt-1">"{review.review_text}"</p>
                )}
              </div>

              {/* Vendor Reply Section */}
              {review.vendor_reply ? (
                <div className="bg-muted p-3 rounded-md">
                  <p className="text-sm font-medium">Your Reply:</p>
                  <p className="text-sm mt-1">"{review.vendor_reply}"</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Replied on {formatDate(review.replied_at!)}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {replyingTo === review.id ? (
                    <div className="space-y-2">
                      <Textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Write your reply to this customer..."
                        rows={3}
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleReplySubmit(review.id)}
                          disabled={submittingReply}
                        >
                          {submittingReply ? 'Submitting...' : 'Submit Reply'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setReplyingTo(null);
                            setReplyText('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setReplyingTo(review.id)}
                    >
                      Reply to Review
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};